import React, { useRef, type RefObject } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { heroSocials } from '../data/heroStats';
import { closingFooter } from '../data/closingFooter';
import { focusInView } from '../utils/reveal';

/**
 * Footer-row blend-in. This row sits at the very end of the page, so a strict "fully on screen and above
 * the bottom edge" trigger could never be met in some browsers/window sizes (the page can't scroll any
 * further) — the items stayed invisible. A partial trigger always fires once the row is reached.
 */
const ROW = {
  y: 12,
  blur: 12,
  amount: 0.2
};
const ROW_STAGGER = 0.08;
const LINK = 'rounded-sm transition-opacity duration-200 ease-out hover:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white';

/**
 * Closing section / footer: tagline + socials on top, the hero statement with an email pill, the giant
 * faint wordmark behind Pixel (who holds the middle of the screen here), and a quiet footer row.
 * Uses the hero's exact type styles; everything comes into focus as it scrolls into view.
 */
interface ClosingHeroProps {
  /** Pixel's last spot, dead centre of the footer — he jumps here from the corner once it's half in view */
  slotRef: RefObject<HTMLDivElement>;
}
export function ClosingHero({
  slotRef
}: ClosingHeroProps) {
  const reduce = useReducedMotion();
  const small = 'text-[clamp(0.875rem,1.05vw,1.25rem)] font-bold leading-tight tracking-[-0.03em]';
  const footerRef = useRef<HTMLElement>(null);
  // From the footer's top entering the screen until it fully fills it (the page end)
  const {
    scrollYProgress
  } = useScroll({
    target: footerRef,
    offset: ['start end', 'end end']
  });
  const unveilY = useTransform(scrollYProgress, [0, 1], reduce ? ['0%', '0%'] : ['-60%', '0%']);
  return <footer ref={footerRef} aria-label="Contact" // Height follows the width like the reference (~0.62 × width), never taller than the screen
  // Phones: grows with its stacked content; desktop: fixed height like the hero
  // Phones: exactly one screen tall
  // Desktop: at least 80% of the window (min 600px) but free to grow — a fixed height clipped the
  // bottom row (links, address, legal) in shorter windows or with larger browser text
  className="relative h-[100svh] min-h-[560px] w-full overflow-hidden bg-black text-white md:h-auto md:min-h-[max(80vh,600px)]" data-id="element-13" data-mp="385gs5drbvfyc">
      {/* Unveil: the content starts pushed up behind the work images above and lags the scroll heavily,
          settling into place only as the footer fully arrives — so the images seem to lift off it */}
      {/* Phones: a two-column grid, mostly full-width rows, in order — Pixel (centred ~40% down the screen),
          Let's talk + email, page links, socials, address, copyright, then logo left / legal right
          (the row wrappers dissolve with `contents`) */}
      <motion.div
    // Phones: the first row (Pixel's spot) takes whatever height is left once the content below fits
    className="relative grid h-full grid-cols-2 grid-rows-[auto_minmax(0,1fr)] gap-x-4 gap-y-6 px-3 md:flex md:h-auto md:min-h-[max(80vh,600px)] md:flex-col pb-[clamp(2.5rem,4.5vw,4.5rem)] pt-28 md:pt-[clamp(3rem,6vw,6rem)] will-change-transform md:gap-0 md:px-[1.6vw]" style={{
      y: unveilY
    }} data-id="element-14" data-mp="72m2s46sr61l">
      {/* Top row: statement + email pill left, socials right — the statement's first line shares the socials' top edge */}
      <div className="contents md:relative md:z-30 md:flex md:flex-wrap md:items-start md:justify-between md:gap-x-6 md:gap-y-4" data-id="element-15" data-mp="ls1rlemrp1en">
      {/* Phones: first, in the top-left corner */}
      <div className="relative z-30 order-1 col-span-2 md:order-1" data-id="element-16" data-mp="c0xfcmoh7uni">
        {/* Same type as "Most brands chase attention. We build meaning." */}
        <p className="text-[clamp(2.5rem,4.85vw,6.5rem)] font-semibold leading-[1.02] tracking-[-0.045em]" data-id="element-17" data-mp="19ixf3zk6xag3">
          {closingFooter.title.map((line, li) => <motion.span key={line} className="block w-fit" {...focusInView(0.15 + li * 0.15, reduce, {
              y: 14,
              blur: 14,
              amount: 1
            })} data-id="element-18" data-mp="o9bqa50hpkqe" data-mpi={li}>
              {line}
            </motion.span>)}
        </p>
        {/* Same button as the hero nav (size, type, hover fade on the wrapper); inverted to white for the black footer */}
        <div className="mt-[clamp(1rem,1.6vw,1.75rem)] w-fit transition-opacity duration-300 ease-[cubic-bezier(0.25,1,0.5,1)] focus-within:opacity-[0.7] [@media(hover:hover)]:hover:opacity-[0.7] motion-reduce:transition-none" data-id="element-19" data-mp="kb4kg57ke3tz">
          <motion.a href={`mailto:${closingFooter.email}`} className="inline-flex h-[clamp(2.5rem,3.2vw,3rem)] items-center whitespace-nowrap rounded-full bg-white px-[clamp(1.25rem,1.8vw,1.75rem)] text-[clamp(0.875rem,1.15vw,1.05rem)] font-semibold leading-[1.2] tracking-[-0.03em] text-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white" {...focusInView(0.45, reduce, {
              y: 12,
              blur: 12,
              amount: 1
            })} data-id="element-20" data-mp="y9kpzstoxoaj">
            {closingFooter.email}
          </motion.a>
        </div>
      </div>
        <motion.nav aria-label="Social" className={`relative z-30 order-4 col-span-2 w-full md:order-2 md:w-auto md:text-right ${small}`} {...focusInView(0.1, reduce, {
          y: 12,
          blur: 12,
          amount: 1
        })} data-id="element-21" data-mp="2essb123btce6">
          {heroSocials.map((link, i) => <React.Fragment key={link.label} data-id="element-22" data-mp="1m4wgo418bwkk" data-mpi={i}>
              {i > 0 && <span aria-hidden="true" className="px-[0.6em] text-neutral-500" data-id="element-23" data-mp="1i3kl7hvoch4g">
                  /
                </span>}
              <a href={link.href} target="_blank" rel="noopener noreferrer" className={LINK} data-id="element-24" data-mp="1w0hd7mnj44mx">
                {link.label}
              </a>
            </React.Fragment>)}
        </motion.nav>
      </div>

      {/* Giant faint wordmark, centered behind Pixel */}
      {/* Phones: vertical like the hero — centred, running from the top of the footer to the bottom */}
      <div className="pointer-events-none absolute inset-y-0 left-1/2 z-10 -translate-x-1/2 py-2 md:hidden" aria-hidden="true" data-id="element-25" data-mp="3aik9qamzfh0c">
        <motion.svg viewBox="-206 0 210 1000" preserveAspectRatio="xMidYMid meet" className="block h-full w-auto" focusable="false" {...focusInView(0, reduce, {
          y: 0,
          blur: 16,
          duration: 1.8,
          amount: 0.2
        })} data-id="element-26" data-mp="3en0da594icce">
          <text x="0" y="198" transform="rotate(90)" textLength="1000" lengthAdjust="spacingAndGlyphs" className="fill-white"
          // Phones: a touch fainter than desktop's 8% — the vertical column is much larger on screen
          fillOpacity={0.06} style={{
            fontFamily: "'Clash Display', sans-serif",
            fontWeight: 700,
            fontSize: 280,
            letterSpacing: '-0.02em'
          }} data-id="element-27" data-mp="2qwnuu0g94x7k">
            LAEBEL’
          </text>
        </motion.svg>
      </div>
      {/* Desktop: horizontal, edge to edge, level with Pixel */}
      <div className="pointer-events-none absolute inset-x-0 top-1/2 z-10 hidden -translate-y-1/2 overflow-hidden px-[1.28vw] md:block" aria-hidden="true" data-id="element-28" data-mp="25jx3urymdvv4">
        <motion.svg viewBox="0 -4 1000 210" className="block h-auto w-full" focusable="false" {...focusInView(0, reduce, {
          y: 0,
          blur: 16,
          duration: 1.8,
          amount: 0.4
        })} data-id="element-29" data-mp="1vlzkoivmap9d">
          <text x="0" y="198" textLength="1000" lengthAdjust="spacingAndGlyphs" className="fill-white"
          // A touch stronger than the hero's 1.5%: white on black reads much fainter at the same value
          fillOpacity={0.08} style={{
            fontFamily: "'Clash Display', sans-serif",
            fontWeight: 700,
            fontSize: 280,
            letterSpacing: '-0.02em'
          }} data-id="element-30" data-mp="1mmmhu9200yxm">
            LAEBEL’
          </text>
        </motion.svg>
      </div>

      {/* Pixel's spot: centre of the footer, hero-sized (not a dock spot, so he stays here) */}
      <div ref={slotRef} aria-hidden="true" data-drone-keep-scale="true" data-drone-footer="true"
      // Phones: dead centre of the footer (= the screen), a bit smaller
      data-drone-mobile-scale="0.75"
      // (Phones: 40% down the footer, i.e. 60% up from the bottom)
      className="pointer-events-none absolute inset-x-0 top-[40%] h-[40svh] w-full -translate-y-1/2 md:absolute md:inset-x-0 md:top-1/2 md:order-none md:h-[46vh] md:min-h-[360px] md:-translate-y-1/2" data-id="element-31" data-mp="2mtpzz8cwgn3u" />

      {/* Phones: empty row that pushes the left-hand footer items down to the bottom */}
      <div aria-hidden="true" className="order-2 col-span-2 md:hidden" data-id="element-32" data-mp="1u5kcweydim0u" />

      {/* Bottom footer row */}
      {/* Each column blends in on its own, left to right, once the row has actually been uncovered */}
      <div className={`contents md:relative md:z-30 md:mt-auto md:grid md:grid-cols-[1fr_1.4fr_1fr_1.2fr_auto] md:items-end md:gap-x-4 md:gap-y-8 ${small}`} data-id="element-33" data-mp="1l44df293k4be">
        <motion.a href="#" aria-label="Back to top" // Phones: bottom-right, stacked above the legal links (pb 2.5rem + ~3 lines + 1.5rem gap)
        className={`${LINK} absolute bottom-[7.5rem] right-3 z-30 w-fit md:relative md:bottom-auto md:right-auto md:order-none`} {...focusInView(0, reduce, ROW)} data-id="element-34" data-mp="16fu98hytgrtf">
          <img src="https://cdn.magicpatterns.com/uploads/iFCAPKP6Hif5VAC8rezXvk/image.png" alt=""
          // White-on-black artwork: screen drops the black backdrop on the dark footer
          className="h-[clamp(3.9rem,5.1vw,6.1rem)] w-auto mix-blend-screen" data-id="element-35" data-mp="1ah62cxgwfv6d" />
        </motion.a>
        {/* Legal notice: quieter than the rest */}
        <motion.p
        // Phones: narrower, so it never runs under the legal links on the right
        className={`relative z-30 order-6 col-span-2 max-w-[58%] text-white/40 md:order-none md:col-span-1 md:max-w-none ${small}`} {...focusInView(ROW_STAGGER, reduce, ROW)} data-id="element-36" data-mp="1u6gjmfwamo6l">
          {closingFooter.copyright}
        </motion.p>
        <motion.nav aria-label="Footer" className={`relative z-30 order-3 col-span-2 md:order-none md:col-span-1 ${small}`} {...focusInView(ROW_STAGGER * 2, reduce, ROW)} data-id="element-37" data-mp="yqplj4eeev3a">
          <ul data-id="element-38" data-mp="py5timsm3orf">
            {closingFooter.links.map(l => <li key={l.label} data-id="element-39" data-mp="kxzw0pznv81f">
                <a href={l.href} className={LINK} data-id="element-40" data-mp="kz2mwxwzhahc">
                  {l.label}
                </a>
              </li>)}
          </ul>
        </motion.nav>
        <motion.address className={`relative z-30 order-5 col-span-2 not-italic text-white/40 md:order-none md:col-span-1 ${small}`} {...focusInView(ROW_STAGGER * 3, reduce, ROW)} data-id="element-41" data-mp="1dmyifzatgwcj">
          {closingFooter.address.map(line => <span key={line} className="block" data-id="element-42" data-mp="13ng1zsu47sp8">
              {line}
            </span>)}
        </motion.address>
        <motion.ul aria-label="Legal" // Phones: pinned to the bottom-right corner, under the logo
        className={`absolute bottom-[clamp(2.5rem,4.5vw,4.5rem)] right-3 z-30 text-right text-white/40 md:relative md:bottom-auto md:right-auto md:order-none ${small}`} {...focusInView(ROW_STAGGER * 4, reduce, ROW)} data-id="element-43" data-mp="3tfe72ugb42gc">
          {closingFooter.legal.map(l => <li key={l.label} data-id="element-44" data-mp="3o8pkozj4efiu">
              {/* Quiet links brighten on hover instead of fading further */}
              <Link to={l.href} className="rounded-sm transition-colors duration-200 ease-out hover:text-white focus-visible:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white" data-id="element-45" data-mp="3t0r4r7mj5ajf">
                {l.label}
              </Link>
            </li>)}
        </motion.ul>
      </div>
      </motion.div>
    </footer>;
}